import React, { useState, useEffect, useCallback } from "react";
import FormTextField from "components/form/form-textfield";
import { injectIntl, FormattedMessage, FormattedHTMLMessage } from "react-intl";
import { DataGridItem } from "components/data-grid";
import withStyles from "components/with-styles";
import LayoutForm, { getDefaultValues } from "../layout-form";
import FormComposed from "components/form/form-composed";
import styles from "./styles.css.js";
import Typography from "@material-ui/core/Typography";
import PropTypes from "prop-types";
import isEqual from "lodash/isEqual";
import Timeline, { getValidLayouts } from "components/timeline";
import { getElementsVisibleOnMixer } from "pages/graphicset/utils";
import Checkbox from "@material-ui/core/Checkbox";
import { sendToArray, sendToMap } from "pages/graphicset/utils";
import { RadioGroup, RadioButton } from "components/form/form-radiobutton";
import Dialog from "components/dialog";
import { SimpleButton } from "components/buttons";
import uniqueId from "lodash/uniqueId";
import { DEFAULT_DURATION } from "components/timeline";
import FormControlLabel from "@material-ui/core/FormControlLabel";

const NewGraphicsetForm = ({ intl, classes, values, handleChange }) => {
  const [excluded, setExcluded] = useState([]);
  const [selected, setSelected] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedSendTo, setSelectedSendTo] = useState(sendToMap.gallery);

  const handleSendToClick = event => {
    if (selectedSendTo === event.target.value) {
      setSelectedSendTo(sendToMap.gallery);
    } else {
      setSelectedSendTo(event.target.value);
    }
  };

  const handleOpen = useCallback(e => {
    setOpen(true);
    e.stopPropagation();
  }, []);

  const handleClose = useCallback(e => {
    setOpen(false);
    setSelectedSendTo(sendToMap.gallery);
    e.stopPropagation();
  }, []);

  const handleAccept = e => {
    const selectedLayouts = values.layouts.map((l, index) => ({ ...l, index })).filter(l => selected.includes(l.id));
    const validSelectedLayouts = selectedLayouts.filter(
      layout =>
        (layout.format && ["png", "apng"].includes(layout.format)) ||
        (layout.type && layout.type === "image" && layout.asset && layout.asset.image),
    );

    validSelectedLayouts.forEach(layout => {
      const layoutPath = `layouts.${layout.index}`;
      handleChange(`${layoutPath}.sendTo`)(selectedSendTo);

      if (sendToMap.banner === selectedSendTo && (!layout.segments || layout.segments.length <= 0)) {
        handleChange(`${layoutPath}.segments`)(
          values.schedules.map(schedule => ({
            id: uniqueId(),
            type: schedule.type,
            start: 0,
            end: DEFAULT_DURATION,
          })),
        );
      }
    });

    handleClose(e);
  };

  const validLayouts = getValidLayouts(values.layouts);
  const showTimeline = validLayouts && validLayouts.length > 0 && values.schedules && values.schedules.length > 0;

  const addToExclusions = (value, oldValue) => {
    const filteredExcluded = oldValue ? excluded.filter(e => e !== oldValue) : excluded;
    setExcluded([...filteredExcluded, value]);
  };

  const removeFromExclusions = value => {
    setExcluded([...excluded.filter(e => e !== value)]);
  };

  useEffect(
    () => {
      const assetIds = values.layouts
        ? values.layouts.filter(layout => layout.assetId).map(layout => layout.assetId)
        : [];
      if (!isEqual(assetIds.sort(), excluded.sort())) {
        setExcluded(assetIds);
      }
    },
    [excluded, values],
  );

  return (
    <>
      <DataGridItem big>
        <FormTextField
          type="text"
          id="name"
          name="name"
          value={values.name}
          onChange={handleChange}
          label={intl.formatMessage({ id: "common.name" })}
        />
      </DataGridItem>
      <DataGridItem big>
        <FormTextField
          type="text"
          id="description"
          name="description"
          value={values.description}
          onChange={handleChange}
          label={intl.formatMessage({ id: "common.description" })}
        />
      </DataGridItem>
      <DataGridItem big>
        <FormattedHTMLMessage
          id="graphicset.mixer.slot.error"
          values={{
            slots: getElementsVisibleOnMixer(values),
          }}
        />
      </DataGridItem>
      {showTimeline && (
        <DataGridItem big>
          <Typography className={classes.subtitle} variant="h6" gutterBottom component="h1">
            <FormattedMessage id="graphicset.program" />
          </Typography>
          {values.schedules.map(currentSchedule => (
            <Timeline
              key={currentSchedule.type}
              onChange={handleChange}
              currentSchedule={currentSchedule}
              schedules={values.schedules}
              layouts={values.layouts}
              customerId={values.customerId}
            />
          ))}
        </DataGridItem>
      )}
      <DataGridItem big>
        <Typography className={classes.subtitle} variant="h6" gutterBottom component="h1">
          <FormattedMessage id="graphicset.layouts" />
        </Typography>
      </DataGridItem>
      {values.layouts.length > 0 && (
        <DataGridItem big>
          <div className={classes.selectAll}>
            <FormControlLabel
              label={intl.formatMessage({ id: "graphicset.layout.selectAll" })}
              control={
                <Checkbox
                  id="selectAllLayouts"
                  name="selectAllLayouts"
                  value="selectAllLayouts"
                  checked={selected.length > 0 && selected.length === values.layouts.length}
                  onChange={event => (event.target.checked ? setSelected(values.layouts.map(l => l.id)) : null)}
                />
              }
            />
          </div>
          <div className={classes.selectAll}>
            <SimpleButton
              onClick={handleOpen}
              label={intl.formatMessage({ id: "graphicset.layout.changeAll" })}
              rel="noopener noreferrer"
              variant="outlined"
            />
            <Dialog
              open={open}
              onAccept={handleAccept}
              onClose={handleClose}
              title={intl.formatMessage({ id: "graphicset.layout.changeAll" })}
            >
              <RadioGroup
                style={{ display: "flex" }}
                name="selectedSendTo"
                selectedValue={selectedSendTo}
                label={intl.formatMessage({ id: "graphicset.layout.sendTo" })}
              >
                {sendToArray
                  .filter(element => element != sendToMap.gallery)
                  .map(sendToElement => (
                    <RadioButton
                      key={sendToElement}
                      name="selectedSendTo"
                      value={sendToElement}
                      onClick={handleSendToClick}
                      label={intl.formatMessage({ id: `graphicset.layout.sendTo.in.${sendToElement.toLowerCase()}` })}
                    />
                  ))}
              </RadioGroup>
            </Dialog>
          </div>
        </DataGridItem>
      )}
      <FormComposed
        name="layouts"
        sortable
        onChange={handleChange}
        elements={values.layouts}
        emptyElement={getDefaultValues(values.schedules, values.layouts)}
        elementComponent={LayoutForm}
        elementComponentProps={{
          customerId: values.customerId,
          excluded,
          compacted: values.layouts.length > 5,
          addToExclusions,
          removeFromExclusions,
          graphicsetSchedules: values.schedules,
          isSelected: layout => selected.includes(layout.id),
          onSelected: (layout, checked) =>
            checked
              ? setSelected([...selected.filter(lId => lId !== layout.id), layout.id])
              : setSelected(selected.filter(lId => lId !== layout.id)),
        }}
      />
      {values.layouts && values.layouts.length > 0 && (
        <DataGridItem big>
          <FormattedHTMLMessage
            id="graphicset.mixer.slot.error"
            values={{
              slots: getElementsVisibleOnMixer(values),
            }}
          />
        </DataGridItem>
      )}
    </>
  );
};

NewGraphicsetForm.propTypes = {
  /**
   * Valores del formulario
   */
  values: PropTypes.object,
  /**
   * Callback que se dispara con el cambio del input
   */
  handleChange: PropTypes.func,
};

export default injectIntl(withStyles(styles)(NewGraphicsetForm));
